<template>
  <div class="container">
    <Teleport to="body">
      <ChartTooltip
        v-if="hoveredCell"
        :label="props.tooltipLabel"
        :color="hoveredCell.color"
        :value="
          hoveredCell.value !== undefined && hoveredCell.value !== null
            ? formatter(hoveredCell.value, locale, dataFormat)
            : '-'
        "
        :x="hoveredCell.x"
        :y="hoveredCell.y"
      />
    </Teleport>
    <TableBase
      v-if="table && table.body.length > 0"
      :table="table"
      :sortBy="(i) => table?.headers.left?.[i]?.label?.title ?? ''"
      :sortDirection="matrix?.splitBy === 'LEVEL' ? 'desc' : 'asc'"
      bodyHeight="400px"
      leftWidth="120px"
      layout="fixed"
      rightTextAlign="center"
      caption="Heatmap table"
      @mouseEnterCell="mouseEnterCell"
      @mouseLeaveCell="mouseLeaveCell"
    >
      <template #header-cell="{ cell: { label, id }, position }">
        <template v-if="position !== 'left'">
          <TextBase
            :text="label?.title"
            textWrap="nowrap"
            :type="position === 'right' || position === 'bottom' ? 'label' : 'tiny'"
          />
        </template>
        <template v-else>
          <div
            :style="{ cursor: 'pointer' }"
            @mouseenter="() => addHoveredItem(id, { color: '#fadb14' })"
            @mouseleave="removeLastHoveredItem"
          >
            <TooltipStandardV2
              v-if="label"
              :style="{ height: '100%' }"
              :text="`${label.title}${label?.subtitle ? '\n(' + label.subtitle + ')' : ''}`"
              :disabledTooltip="position !== 'left'"
              heatmap
              arrow-position="top-center"
            >
              <div><TextBase :text="label.title" type="tiny" overflow="ellipsis" /></div>
              <div v-if="label?.subtitle">
                <TextBase :text="label.subtitle" type="tiny" color="secondary" />
              </div>
            </TooltipStandardV2>
          </div>
        </template>
      </template>
      <template #footer>
        <ColorLegendContinuous :formatOptions="dataFormat" :colorScale="colorScale" />
      </template>
    </TableBase>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import TooltipStandardV2 from '../atoms/TooltipStandardV2.vue'
import ChartTooltip from '../atoms/ChartTooltip.vue'
import ColorLegendContinuous from '@/design-system/atoms/ColorLegendContinuous.vue'
import { ColorScaleInterpolate, getColorForValue } from '@/utils/colorScale'
import TableBase from '../atoms/TableBase.vue'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'
import { HeatmapResponse, HeatmapSplitBy } from '@/api-client/models/indoorclimate/Contracts'
import TextBase from '../atoms/TextBase.vue'
import { useEntityLookup } from '@/composables/useEntityLookup'
import { capitalizeFirstLetter } from '@/utils/capitalizeFirstLetter'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { useHoverStore } from '@/pinia/hover'

type Props = {
  matrix?: HeatmapResponse
  xSumLabel?: string
  ySumLabel?: string
  tooltipLabel: string
  fullscreen?: boolean
  colorScale: ColorScaleInterpolate
  dataFormat?: FormatOptions
  timeFormat?: FormatOptions
  start: number
  end: number
  subMeasurementToTitle: Record<string, LocaleString>
}

type HeaderLabel = {
  title: string
  subtitle?: string
}

type Cell = {
  value: number | null
  color: string
  x: number
  y: number
}

const { locale } = useI18n()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()

const props = defineProps<Props>()

const { findEntity } = useEntityLookup()

const table = computed(() =>
  props.matrix
    ? {
        body: props.matrix.matrix.map((row) =>
          paginate(row).map((value) => {
            const color =
              value !== undefined && value !== null
                ? getColorForValue(props.colorScale, value).toHex()
                : '#fff'
            return {
              value,
              color,
              style: { backgroundColor: color },
              class: 'custom-cell'
            }
          })
        ),
        headers: {
          top: paginate(props.matrix.axes.x).map((label) => ({
            label: {
              title: label ? formatter(label, locale.value, props.timeFormat) : '-'
            } as HeaderLabel,
            class: 'label-x'
          })),
          left: props.matrix.axes.y.map((id) => ({
            label:
              props.matrix?.splitBy && id ? translateIdToName(id, props.matrix.splitBy) : undefined,
            id,
            class: 'label-y'
          })),
          bottomLeft: { label: { title: props.xSumLabel } as HeaderLabel, class: 'label-x' },
          bottom:
            props.ySumLabel !== undefined
              ? paginate(props.matrix.summary.x).map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined,
          topRight:
            props.xSumLabel !== undefined
              ? { label: { title: props.ySumLabel } as HeaderLabel, class: 'label-x' }
              : undefined,
          right:
            props.xSumLabel !== undefined
              ? props.matrix.summary.y.map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined
        }
      }
    : undefined
)

const hoveredCell = ref<Cell | undefined>()

const mouseEnterCell = (
  { value, color }: { value: number | null; color: string | undefined },
  centerX: number,
  centerY: number
) => {
  if (value === null || color === undefined) {
    return
  }
  hoveredCell.value = {
    value,
    color,
    x: centerX,
    y: centerY
  }
}

const mouseLeaveCell = () => {
  hoveredCell.value = undefined
}

function translateIdToName(id: string, splitBy: HeatmapSplitBy): HeaderLabel | undefined {
  if (splitBy === 'SUB_MEASUREMENT') {
    return {
      title: props.subMeasurementToTitle?.[id as string]?.[locale.value]
    }
  } else {
    const entity = findEntity(id)
    const entityName = entity?.properties.name
    let subtitle = undefined
    if (splitBy === 'SPACE') {
      subtitle = entity?.properties.aliasId
    } else if (splitBy === 'CONSTRUCTION') {
      subtitle = entity?.properties.marking
    }
    return entity
      ? {
          title: capitalizeFirstLetter(entityName),
          subtitle
        }
      : { title: id }
  }
}

function paginate<T>(array: T[]): T[] {
  return array.slice(Math.max(0, props.start), Math.min(array.length, props.end))
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
.color-legends-box {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding-left: 104px;
}

.container {
  position: relative;
}

.header-label {
  width: 100%;
  overflow-x: hidden;
  text-wrap: nowrap;
  text-overflow: ellipsis;
}

:deep(*) {
  .cell {
    position: relative;
    height: 100%;
    display: flex;
    justify-content: center;
    vertical-align: middle;
  }
  .cell-width {
    width: 104px;
  }
  .custom-table {
    border-collapse: collapse;
  }

  .label-y {
    @include font-mixins.desktop-regular-tiny;
    padding: 8px;
    height: 40px;
    text-align: start;
    text-wrap: nowrap;
    color: variables.$color-greyscale-content;
    text-overflow: ellipsis;
  }

  .label-x {
    @include font-mixins.desktop-bold-tiny;
    padding: 8px;
    padding-top: 16px;
    color: variables.$color-greyscale-secondary;
  }
  .custom-cell {
    @include font-mixins.desktop-regular-label;
    height: 40px;
    text-align: center;
    position: relative;
    border: 1px solid #fff;
    .fullscreen & {
      height: 40px;
    }
  }

  .custom-sum {
    @include font-mixins.desktop-regular-label;
    height: 40px;
    border: 1px solid #fff;
    text-align: center;
    background-color: #f9f9f9;
    color: variables.$color-greyscale-content;
    .fullscreen & {
      height: 40px;
    }
  }
}
</style>
